import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import { IMAGES, SERVICES, TESTIMONIALS, WHY_CHOOSE } from "@/data/content";
import { Reveal } from "@/lib/motion";
import { EditorialMarquee } from "@/components/Marquee";
import * as Icons from "lucide-react";

const heroLines = [
  "Professional Mobile",
  "Wellness Across",
  "Manchester",
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
          <img src={IMAGES.heroBg} alt="Serene spa environment" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ y: overlayY }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-20 sm:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="text-primary-foreground/80 text-xs sm:text-sm tracking-[0.35em] uppercase mb-6 flex items-center gap-2"
          >
            <Sparkles size={16} /> Relax. Recover. Recharge.
          </motion.p>

          <h1 className="font-serif text-primary-foreground text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight max-w-5xl">
            {heroLines.map((line, i) => (
              <span key={i} className="hero-line block">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
            className="text-primary-foreground/85 text-lg sm:text-xl max-w-2xl mt-8 font-light"
          >
            We bring relaxation, recovery and wellbeing directly to your home, hotel or workplace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.7 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to="/booking" data-testid="hero-book-btn" className="group inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-8 py-4 font-semibold hover:bg-secondary/90 transition-colors">
              Book Now <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/contact" data-testid="hero-contact-btn" className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 backdrop-blur-md border border-primary-foreground/30 text-primary-foreground px-8 py-4 font-semibold hover:bg-primary-foreground/20 transition-colors">
              Contact Us
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-36 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold">Risi Wellness Ltd</p>
        </Reveal>
        <Reveal className="lg:col-span-8" delay={0.1}>
          <h2 className="font-serif text-3xl sm:text-5xl leading-tight text-primary">
            Spa-quality treatments, delivered to your door — with the comfort, convenience and care you deserve.
          </h2>
          <p className="text-muted-foreground text-lg mt-8 max-w-2xl leading-relaxed">
            Risi Wellness Limited is a professional wellness company dedicated to improving physical and mental
            wellbeing through high-quality mobile services. We bring professional treatments directly to your home,
            hotel or workplace — no travelling required.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-primary font-semibold hover:gap-3 transition-all">
            Our story <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>

      <EditorialMarquee />

      {/* FEATURED SERVICES — bento */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold mb-3">Featured Treatments</p>
            <h2 className="font-serif text-4xl sm:text-6xl text-primary leading-none">The Signature<br />Collection</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-primary/30 text-primary px-6 py-3 font-semibold hover:bg-primary hover:text-primary-foreground transition-colors">
              View all services <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-12 gap-5">
          {SERVICES.slice(0, 3).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-6 md:row-span-2" : "md:col-span-6"}>
              <Link to={`/services/${s.slug}`} className="group block relative overflow-hidden rounded-2xl h-full min-h-[280px]">
                <img src={s.image} alt={s.name} loading="lazy" decoding="async" className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "h-full min-h-[560px]" : "h-72"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/20 to-transparent" />
                <div className="absolute bottom-0 p-7">
                  <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-2">{s.tagline}</p>
                  <h3 className="font-serif text-primary-foreground text-2xl sm:text-3xl">{s.name}</h3>
                </div>
                <div className="absolute top-5 right-5 p-2.5 rounded-full bg-background/90 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={18} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold mb-3">Why Choose Us</p>
            <h2 className="font-serif text-4xl sm:text-6xl text-primary leading-none mb-14 max-w-2xl">The Risi difference</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = Icons[w.icon] || Icons.Sparkles;
              return (
                <Reveal key={w.title} delay={(i % 4) * 0.06}>
                  <div className="flex flex-col gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Icon size={22} /></div>
                    <h3 className="font-serif text-xl text-primary">{w.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {TESTIMONIALS.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold mb-3 text-center">Client feedback</p>
            <h2 className="font-serif text-4xl sm:text-6xl text-primary leading-none mb-14 text-center">What clients say</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 2) * 0.08}>
                <figure className="h-full rounded-2xl border border-border bg-card p-8 sm:p-10">
                  <blockquote className="font-serif text-2xl sm:text-3xl text-primary leading-snug">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 text-sm"><span className="font-semibold text-foreground">{t.name}</span><span className="text-muted-foreground"> — {t.role}</span></figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl">
            <img src={IMAGES.lakeside} alt="Calm nature" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/70" />
            <div className="relative z-10 px-8 sm:px-16 py-20 sm:py-28 text-center">
              <p className="text-secondary text-xs tracking-[0.3em] uppercase mb-4 flex items-center justify-center gap-2"><MapPin size={15} /> Manchester & surrounding areas</p>
              <h2 className="font-serif text-4xl sm:text-6xl text-primary-foreground leading-tight max-w-3xl mx-auto">Your wellbeing, wherever you are.</h2>
              <Link to="/booking" className="inline-flex mt-10 items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-9 py-4 font-semibold hover:bg-secondary/90 transition-colors">
                Book Your Treatment <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
